import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  Modal,
  Switch,
  TextField,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import {
  useEditPackagesPrice,
  usePackagesPriceById,
} from "@/features/packages-price/use-packages-prices.ts";

interface IFormInput {
  isActive: boolean;
  name: string;
  description: string;
  image: string;
  price: number;
}

export function EditPackagesPricesModal({
  packagesId,
}: {
  packagesId: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { data, refetch } = usePackagesPriceById({
    packagesId,
    enabled: false,
  });
  const { mutateAsync, isPending } = useEditPackagesPrice();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    mutateAsync({
      id: Number(packagesId),
      isActive: data.isActive,
      name: data.name.trim(),
      description: data.description.trim(),
      image: data.image.trim(),
      price: Number(data.price),
    }).then(() => {
      reset();
      setIsOpen(false);
    });
  };

  return (
    <div>
      <Button
        isIconOnly
        variant="ghost"
        onPress={() => refetch().then(() => setIsOpen(true))}
      >
        <Icon className="size-6" icon="solar:pen-bold-duotone" />
      </Button>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center">
            <Modal.Dialog>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header>
                  <Modal.Heading>Изменить упаковку</Modal.Heading>
                </Modal.Header>

                {data?.data && (
                  <Modal.Body>
                    <TextField
                      defaultValue={data.data.name}
                      isInvalid={!!errors.name?.message}
                    >
                      <Label>Название</Label>
                      <Input
                        {...register("name", {
                          required: "Нужно заполнить поле",
                        })}
                      />
                      <FieldError>{errors.name?.message}</FieldError>
                    </TextField>

                    <TextField
                      defaultValue={data.data.description}
                      isInvalid={!!errors.description?.message}
                    >
                      <Label>Описание</Label>
                      <Input
                        {...register("description", {
                          required: "Нужно заполнить поле",
                        })}
                      />
                      <FieldError>{errors.description?.message}</FieldError>
                    </TextField>

                    <TextField
                      defaultValue={data.data.image}
                      isInvalid={!!errors.image?.message}
                    >
                      <Label>Изображение</Label>
                      <Input
                        {...register("image", {
                          required: "Нужно заполнить поле",
                        })}
                      />
                      <FieldError>{errors.image?.message}</FieldError>
                    </TextField>

                    <TextField
                      defaultValue={`${data.data.price}`}
                      isInvalid={!!errors.price?.message}
                    >
                      <Label>Цена</Label>
                      <Input
                        {...register("price", {
                          required: "Нужно заполнить поле",
                        })}
                      />
                      <FieldError>{errors.price?.message}</FieldError>
                    </TextField>

                    <Controller
                      control={control}
                      defaultValue={data.data.isActive}
                      name="isActive"
                      render={({ field }) => (
                        <Switch
                          isSelected={field.value}
                          onChange={field.onChange}
                        >
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                          <Switch.Content>
                            <Label>Активно</Label>
                          </Switch.Content>
                        </Switch>
                      )}
                    />
                  </Modal.Body>
                )}

                <Modal.Footer>
                  <Button
                    variant="danger-soft"
                    onPress={() => setIsOpen(false)}
                  >
                    Отмена
                  </Button>

                  <Button isPending={isPending} type="submit" variant="primary">
                    Сохранить
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
